'use client';

import type { ClubOperatingHourSlot } from '@court-sync/shared';
import { Input, Switch } from '@/components/ui';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export interface DayScheduleRow {
  dayOfWeek: number;
  isOpen: boolean;
  startTime: string;
  endTime: string;
}

export function toDayScheduleRows(slots: ClubOperatingHourSlot[]): DayScheduleRow[] {
  const slotMap = new Map(slots.map((slot) => [slot.dayOfWeek, slot]));
  return DAYS.map((_, dayOfWeek) => {
    const slot = slotMap.get(dayOfWeek);
    if (slot) {
      return {
        dayOfWeek,
        isOpen: true,
        startTime: slot.startTime.slice(0, 5),
        endTime: slot.endTime.slice(0, 5),
      };
    }
    return {
      dayOfWeek,
      isOpen: false,
      startTime: '09:00',
      endTime: '17:00',
    };
  });
}

export function dayScheduleRowsToSlots(rows: DayScheduleRow[]): ClubOperatingHourSlot[] {
  return rows
    .filter((row) => row.isOpen)
    .map((row) => ({
      dayOfWeek: row.dayOfWeek,
      startTime: row.startTime,
      endTime: row.endTime,
    }));
}

interface OperatingHoursScheduleEditorProps {
  rows: DayScheduleRow[];
  onChange: (rows: DayScheduleRow[]) => void;
  idPrefix?: string;
}

export function OperatingHoursScheduleEditor({
  rows,
  onChange,
  idPrefix = 'hours',
}: OperatingHoursScheduleEditorProps) {
  const updateRow = (dayOfWeek: number, patch: Partial<DayScheduleRow>) => {
    onChange(rows.map((row) => (row.dayOfWeek === dayOfWeek ? { ...row, ...patch } : row)));
  };

  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <div
          key={row.dayOfWeek}
          className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700"
        >
          <span className="w-28 text-sm font-medium text-gray-900 dark:text-white">
            {DAYS[row.dayOfWeek]}
          </span>
          <div className="flex items-center gap-2">
            <Switch
              id={`${idPrefix}-day-open-${row.dayOfWeek}`}
              checked={row.isOpen}
              onCheckedChange={(checked) => updateRow(row.dayOfWeek, { isOpen: checked })}
            />
            <label
              htmlFor={`${idPrefix}-day-open-${row.dayOfWeek}`}
              className="text-sm text-gray-600 dark:text-gray-400"
            >
              Open
            </label>
          </div>
          {row.isOpen ? (
            <div className="flex flex-wrap items-center gap-2">
              <Input
                type="time"
                value={row.startTime}
                onChange={(e) => updateRow(row.dayOfWeek, { startTime: e.target.value })}
                aria-label={`${DAYS[row.dayOfWeek]} start time`}
                className="w-[8.5rem]"
              />
              <span className="text-sm text-gray-500">to</span>
              <Input
                type="time"
                value={row.endTime}
                onChange={(e) => updateRow(row.dayOfWeek, { endTime: e.target.value })}
                aria-label={`${DAYS[row.dayOfWeek]} end time`}
                className="w-[8.5rem]"
              />
            </div>
          ) : (
            <span className="text-sm text-gray-500 dark:text-gray-400">Closed</span>
          )}
        </div>
      ))}
    </div>
  );
}
